import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

// --- TYPE DEFINITIONS ---

interface MetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  badge?: string;
  metrics: {
    value: string | number;
    label?: string;
  }[];
  metaStatement?: string;
  actionLabel?: string;
  onAction?: () => void;
  variant?: 'default' | 'compact';
}

// --- MAIN COMPONENT ---

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  badge,
  metrics = [],
  metaStatement,
  actionLabel = 'Primary',
  onAction,
  variant = 'default',
  className = '',
  ...props
}) => {
  return (
    <div
      className={cn(
        'bg-sky-50 flex flex-col gap-6 p-4 relative rounded-2xl',
        'border border-gray-200 shadow-[0px_0px_27.878px_18.585px_#e5e7eb]',
        className
      )}
      {...props}
    >
      {/* Header */}
      <div className="flex flex-col gap-4 w-full">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <h3 className="text-[18px] font-medium text-sky-900 font-['Roboto_Condensed',_sans-serif] leading-[23px]">
              {title}
            </h3>
          </div>
          {badge && (
            <Badge 
              variant="secondary" 
              className="bg-sky-100 text-sky-700 border-0 px-3 h-7 text-[14px] font-semibold font-['Roboto_Condensed',_sans-serif] rounded-full"
            >
              {badge}
            </Badge>
          )}
        </div>
      </div>

      {/* Separator */}
      <Separator className="bg-gray-200 h-px -my-2.5" />

      {/* Metrics Section */}
      {metrics.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline gap-2">
            {metrics.map((metric, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <span className="text-[24px] font-semibold text-sky-900 font-['Roboto_Condensed',_sans-serif] leading-[31px]">
                    •
                  </span>
                )}
                <div className="flex items-baseline gap-1">
                  <span className="text-[24px] font-semibold text-sky-900 font-['Roboto_Condensed',_sans-serif] leading-[31px]">
                    {metric.value}
                  </span>
                  {metric.label && (
                    <span className="text-[24px] font-semibold text-sky-900 font-['Roboto_Condensed',_sans-serif] leading-[31px]">
                      {metric.label}
                    </span>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
          {metaStatement && (
            <p className="text-[16px] font-normal text-gray-500 font-['Roboto_Condensed',_sans-serif] leading-[24px]">
              {metaStatement}
            </p>
          )}
        </div>
      )}

      {/* Footer with Action Button */}
      {onAction && (
        <div className="flex justify-end w-full">
          <Button
            onClick={onAction}
            className="bg-sky-100 hover:bg-sky-200 text-sky-700 border border-sky-200 shadow-[4px_20px_40px_0px_rgba(0,0,0,0.03)] h-9 px-3 text-[14px] font-medium font-['Roboto_Condensed',_sans-serif] rounded-lg"
          >
            {actionLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

MetricCard.displayName = 'MetricCard';

export { MetricCard };
export default MetricCard;
export type { MetricCardProps };
